import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { extractTokenFromUrl, decryptTokenViaApi, EncodedDataParams, STORAGE_KEY_MAPPINGS } from '../utils/sessionUtils';

const TestParamsPage: React.FC = () => {
  const [params, setParams] = useState<EncodedDataParams>({});
  const [jsonString, setJsonString] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  
  useEffect(() => {
    // Extract parameters from URL via API decryption
    const loadParams = async () => {
      const token = extractTokenFromUrl();
      if (token) {
        setLoading(true);
        try {
          const extractedParams = await decryptTokenViaApi(token);
          if (extractedParams) {
            setParams(extractedParams);
          } else {
            setError('Failed to decrypt token');
          }
        } catch (e) {
          setError('Error decrypting token');
        } finally {
          setLoading(false);
        }
      }
    };
    loadParams();
    
    // Set sample JSON for reference
    const sampleJsonData = {
      AgentName: 'Central Clinic',
      AgencyEmail: 'user@example.com'
    };
    setJsonString(JSON.stringify(sampleJsonData, null, 2));
  }, []);
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">URL Parameter Test Page</h1>
      
      <div className="bg-gray-100 p-4 rounded-lg mb-6">
        <h2 className="text-lg font-semibold mb-2">Current URL Parameters:</h2>
        {loading && <p>Decrypting token...</p>}
        {error && <p className="text-red-600">{error}</p>}
        {!loading && !error && Object.keys(params).length === 0 && (
          <p>No parameters found in the URL</p>
        )}
        {!loading && Object.keys(params).length > 0 && (
          <ul className="list-disc pl-6">
            {Object.entries(params).map(([key, value]) => (
              <li key={key}><strong>{key}:</strong> {value}</li>
            ))}
          </ul>
        )}
      </div>
      
      <div className="bg-blue-50 p-4 rounded-lg mb-6">
        <h2 className="text-lg font-semibold mb-2">Test with Encrypted Token:</h2>
        <div className="bg-white p-3 rounded border">
          <p className="text-sm text-gray-700 mb-2">
            This page now requires an AES-256-GCM encrypted token from the PHP backend.
          </p>
          <p className="text-sm text-gray-700">
            To test, append <code>?token=YOUR_ENCRYPTED_TOKEN</code> to the URL.
            <br />
            Example: <code>{window.location.origin}/test-params?token=xxx</code>
          </p>
        </div>
      </div>
      
      <div className="bg-yellow-50 p-4 rounded-lg mb-6">
        <h2 className="text-lg font-semibold mb-2">JSON Data Reference:</h2>
        <div className="bg-white p-3 rounded border font-mono text-sm overflow-auto">
          {jsonString}
        </div>
        <h3 className="text-md font-semibold mt-4 mb-2">Storage Key Mappings:</h3>
        <ul className="list-disc pl-6">
          {Object.entries(STORAGE_KEY_MAPPINGS).map(([jsonKey, storageKey]) => (
            <li key={jsonKey}>
              <strong>JSON Key:</strong> {jsonKey}<br />
              <strong>Storage Key:</strong> {storageKey}
            </li>
          ))}
        </ul>
      </div>
      
      <div className="flex gap-4">
        <Link 
          to="/" 
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
        >
          Back to Home
        </Link>
        <Link 
          to="/app/recorb" 
          className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 rounded transition-colors"
        >
          Go to RecOrb Form
        </Link>
      </div>
    </div>
  );
};

export default TestParamsPage;
